/**
 * What a turn's fold line says (S-509): how long it worked, how many steps,
 * and the ticking "Working for 12s" while it runs.
 */
import { useEffect, useState } from "react";
import { duration } from "../activity/activity-view";
import type { Turn } from "./api";

/** How long a turn worked, from its start to its end; null when it never started. */
export function workedMs(turn: Pick<Turn, "started_at" | "finished_at">): number | null {
  if (!turn.started_at || !turn.finished_at) return null;
  return Math.max(0, Date.parse(turn.finished_at) - Date.parse(turn.started_at));
}

export const stepCount = (n: number) => `${n} ${n === 1 ? "step" : "steps"}`;

/**
 * The fold line's words: "Worked for 37s · 9 steps", or a check-in's quieter
 * "9 steps · 21s". The failed count is the line's own, in brick.
 */
export function workSummary(turn: Turn, quiet: boolean): string {
  const n = turn.work?.steps ?? 0;
  const ms = workedMs(turn);
  if (quiet) return ms === null ? stepCount(n) : `${stepCount(n)} · ${duration(ms)}`;
  return ms === null ? stepCount(n) : `Worked for ${duration(ms)} · ${stepCount(n)}`;
}

/** "Working for 12s" while a turn runs, ticking each second; "Working" for its first second. */
export function useWorkingLabel(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return "Working";
  const ms = now - Date.parse(startedAt);
  return ms < 1000 ? "Working" : `Working for ${duration(ms)}`;
}
